import { Components } from 'appworks/components/components';
import { PromptComponent } from 'appworks/components/prompt/prompt-component';
import { PromptContentSubcomponent } from 'appworks/components/prompt/prompt-content-subcomponent';
import { gameState } from 'appworks/model/game-state';
import { CurrencyService } from 'appworks/services/currency/currency-service';
import { Services } from 'appworks/services/services';
import { SoundService } from 'appworks/services/sound/sound-service';
import { TranslationsService } from 'appworks/services/translations/translations-service';
import { Contract } from 'appworks/utils/contracts/contract';
import { BlastworksClientEvent, ClientController } from 'client-controller';
import { gameLayers } from 'game-layers';
import { SlingoCompletionReason } from 'slingo/integration/slingo-schema';
import { SlingoGameProgressResult } from 'slingo/model/results/slingo-game-progress-result';
import { SlingoSoundEvent } from 'slingo/sound/slingo-sound-events';
import { BigWinComponent } from 'slotworks/components/bigwin/big-win-component';
import { FowlGameOverState } from 'state-machine/states/fowl-game-over-state';

/**
 * Social game over state to send game over message
 *
 * @class SocialGameOverState
 * @typedef {SocialGameOverState}
 * @extends {PYLGameOverState}
 */
export class SocialGameOverState extends FowlGameOverState {
  /**
   * Send game over message
   *
   * @public
   * @override
   * @param {?boolean} [cascadeSkip]
   */
  public onEnter(cascadeSkip?: boolean): void {
    ClientController.getInstance().raiseEvent(BlastworksClientEvent.GAME_OVER);
    super.onEnter(cascadeSkip);
  }

  protected showWinSummary(): Contract<void> {
    Services.get(SoundService).customEvent(SlingoSoundEvent.end_game_win);

    const translations = Services.get(TranslationsService);
    const currency = Services.get(CurrencyService);

    const gameplay = gameState.getCurrentGame();
    const gameProgress = gameplay.getLatestResultOfType(SlingoGameProgressResult);
    const totalWin = gameProgress.totalWin;
    const totalStake = gameplay.getTotalWagered();
    const meetsWinRequirement = this.meetsWinRequirement();

    const isBigWin = Components.get(BigWinComponent).isBigWin(totalWin, totalStake);
    const isFullHouse = gameplay.getLatestResultOfType(SlingoGameProgressResult).completionReason === SlingoCompletionReason.TICKET_MATCHED;

    /** Title */
    let titleKey = "results_caps";
    let pref = "result";
    if (isFullHouse && meetsWinRequirement) {
      // titleKey = "full_house_caps";
      pref = "megawin";
    } else if (isBigWin && meetsWinRequirement) {
      // titleKey = "big_win_caps";
      pref = "bigwin";
    } else if (meetsWinRequirement) {
      // titleKey = "win_caps";
      pref = "win";
    }

    // create the prompt
    const summaryPrompt = new PromptContentSubcomponent(["gamesummary_win"], 5000);

    summaryPrompt.setText("title", translations.get(titleKey));
    summaryPrompt.setText("win_breakdown", this.getWinBreakdown());
    // summaryPrompt.setText("total_win", currency.format(gameProgress.totalWin));
    summaryPrompt.setText("total_win", `${translations.get("total_win")}: ${currency.format(totalWin)}`); // carried over from slingo-game-over-state

    gameLayers.Prompts.onSceneEnter.addOnce((scene) => this.onSceneEnter(scene));
    gameLayers.Prompts.afterSceneEnter.addOnce((scene) => this.afterSceneEnter(scene, pref));

    return Components.get(PromptComponent).showPrompt(summaryPrompt);
  }

  /**
   * This is analogous to GameSummaryCells[]
   */
  protected override getWinBreakdown(): string {
    let text = super.getWinBreakdown();
    // Remove the "Slingos" line
    const firstNewline = text.indexOf("\n");
    if (firstNewline !== -1) {
      text = text.substring(firstNewline + 1);
    }
    return text;
  }


  protected override onSceneEnter(scene): void {
    // hide the second copy of "Total Win"
    const { total_win_label } = gameLayers.Prompts.scene.gamesummary_win.contents;
    total_win_label.visible = false; // no text, yellow numbers
    // total_win_label.visible = true; // white title, yellow numbers

    super.onSceneEnter(scene);
  }
}
